import { useMutation, useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { contactsApi } from '../../api/contacts'
import { outboxApi } from '../../api/outbox'
import { RefusedError } from '../../api/client'
import { formatDate, formatMoney } from '../../lib/format'
import type { ReferralSource } from '../../types/contacts'

// Who sends you work (REQ-072). Don, 2026-09-23: "I could go back to Leon and say how many referrals do you
// have? … and I can also see who my referral sources were that actually made me money too." The totals are
// his inputs (asked / received / given); the table is the people, ranked by the won work their referrals
// became. A source is a person relationship the mail named — never a profession (DEC-2026-09-23-07).
// "Ask for a referral" drafts ONE message to ONE person into the outbox; nothing is sent from here.

export function ReferralSources({ spoke, onOpen }: { spoke: string; onOpen: (personId: string) => void }) {
  const navigate = useNavigate()
  const q = useQuery({ queryKey: ['referral-sources', spoke], queryFn: () => contactsApi.referralSources(spoke), staleTime: 30_000 })
  const ask = useMutation({ mutationFn: (personId: string) => outboxApi.compose(spoke, personId, 'referral-ask'), onSuccess: () => navigate('/outbox') })

  if (q.isPending) return <p className="muted">Reading your referrals…</p>
  if (q.isError) return <div className="refusal"><strong>Unavailable</strong><p>{q.error instanceof RefusedError ? q.error.refusal.message : q.error.message}</p></div>
  const r = q.data
  return (
    <section className="card referral-sources">
      <header className="card-head">
        <div>
          <h2>Who sends you work</h2>
          <p className="muted small">Since {formatDate(r.since)} · read from your own mail. A source is a person who sent work your way; the money is the won work it became.</p>
        </div>
        <dl className="glance-conventions">
          <dt>Asked</dt><dd><span className="chip"><em>{r.totals.asked}</em></span></dd>
          <dt>Received</dt><dd><span className="chip"><em>{r.totals.received}</em></span></dd>
          <dt>Given</dt><dd><span className="chip"><em>{r.totals.given}</em></span></dd>
        </dl>
      </header>
      {ask.isError && <p className="error">{ask.error instanceof RefusedError ? ask.error.refusal.message : ask.error.message}</p>}
      {r.sources.length === 0
        ? <p className="muted">No referral on file yet. When a thread says somebody sent work your way, they appear here with what it was worth.</p>
        : (
          <table className="grid">
            <thead>
              <tr><th>Person</th><th>Company</th><th>Sent you</th><th>Last</th><th>Won</th><th>Revenue</th><th></th></tr>
            </thead>
            <tbody>
              {r.sources.map((s) => <SourceRow key={s.personId} s={s} onOpen={() => onOpen(s.personId)} onAsk={() => ask.mutate(s.personId)} asking={ask.isPending} />)}
            </tbody>
          </table>
        )}
    </section>
  )
}

function SourceRow({ s, onOpen, onAsk, asking }: { s: ReferralSource; onOpen: () => void; onAsk: () => void; asking: boolean }) {
  return (
    <tr>
      <td><button className="link-button" onClick={onOpen}>{s.name}</button></td>
      <td>{s.companyName ?? <span className="muted">—</span>}</td>
      <td>{s.referralsReceived}{s.referralsGiven > 0 && <span className="muted small"> · you introduced {s.referralsGiven}</span>}</td>
      <td>{s.lastReferralOn ? formatDate(s.lastReferralOn) : <span className="muted">—</span>}</td>
      <td>{s.wonCount}</td>
      <td>{s.revenueAttributed > 0 ? formatMoney(s.revenueAttributed) : <span className="muted">0</span>}</td>
      <td><button className="button small" onClick={onAsk} disabled={asking} title="Drafts one message to this person asking for a referral, into your outbox. Nothing is sent.">Ask for a referral</button></td>
    </tr>
  )
}
